import type { Token } from 'typescript-parsec';
import { alt_sc, apply, lrec_sc, rule, seq, tok } from 'typescript-parsec';

import type { AST } from './ast';
import { TokenKind } from './lexer';

export const EXPRESSION = rule<TokenKind, AST>();

const STATUS = rule<TokenKind, AST>();
const CONDITIONAL = rule<TokenKind, AST>();
const CONDITION = rule<TokenKind, AST>();
const COMPARISON = rule<TokenKind, AST>();
const TERM = rule<TokenKind, AST>();
const NUMBER = rule<TokenKind, AST>();
const ARITHMETIC = rule<TokenKind, AST>();
const PRIORITY_ARITHMETIC = rule<TokenKind, AST>();
const IDENTIFIERS = rule<TokenKind, AST>();

/*
EXPRESSION
  = STATUS
  = CONDITIONAL
*/
EXPRESSION.setPattern(alt_sc(STATUS, CONDITIONAL));

/*
STATUS
  = "active"
  = "paused"
  = "no-change"
*/
STATUS.setPattern(
    apply(alt_sc(tok(TokenKind.StatusActive), tok(TokenKind.StatusPaused), tok(TokenKind.StatusSkip)), applyStatus),
);

/*
CONDITIONAL
  = "if" CONDITION "then" EXPRESSION "else" EXPRESSION
*/
CONDITIONAL.setPattern(
    apply(
        seq(
            tok(TokenKind.KeywordIf),
            CONDITION,
            tok(TokenKind.KeywordThen),
            EXPRESSION,
            tok(TokenKind.KeywordElse),
            EXPRESSION,
        ),
        applyConditional,
    ),
);

/*
CONDITION
  = COMPARISON
  = COMPARISON ("and" | "or") CONDITION
  = "(" CONDITION ")"
*/
CONDITION.setPattern(
    alt_sc(
        lrec_sc(
            COMPARISON,
            seq(alt_sc(tok(TokenKind.KeywordAnd), tok(TokenKind.KeywordOr)), CONDITION),
            applyCombination,
        ),
        apply(seq(tok(TokenKind.KeywordNot), CONDITION), applyNot),
        apply(seq(tok(TokenKind.SymbolOpenParen), CONDITION, tok(TokenKind.SymbolCloseParen)), applyParens),
    ),
);

/*
COMPARISON
  = ARITHMETIC ("<" | ">" | "<=" | ">=") ARITHMETIC
  = "(" COMPARISON ")"
*/
COMPARISON.setPattern(
    alt_sc(
        apply(
            seq(
                ARITHMETIC,
                alt_sc(
                    tok(TokenKind.SymbolLessThan),
                    tok(TokenKind.SymbolGreaterThan),
                    tok(TokenKind.SymbolLessThanEqual),
                    tok(TokenKind.SymbolGreaterThanEqual),
                ),
                ARITHMETIC,
            ),
            applyComparison,
        ),
        apply(seq(tok(TokenKind.SymbolOpenParen), COMPARISON, tok(TokenKind.SymbolCloseParen)), applyParens),
    ),
);

/*
ARITHMETIC
  = PRIORITY_ARITHMETIC
  = PRIORITY_ARITHMETIC ("+" | "-") ARITHMETIC
  = "(" ARITHMETIC ")"
*/
ARITHMETIC.setPattern(
    alt_sc(
        lrec_sc(
            PRIORITY_ARITHMETIC,
            seq(alt_sc(tok(TokenKind.SymbolPlus), tok(TokenKind.SymbolMinus)), ARITHMETIC),
            applyBinary,
        ),
        apply(seq(tok(TokenKind.SymbolOpenParen), ARITHMETIC, tok(TokenKind.SymbolCloseParen)), applyParens),
    ),
);

/*
PRIORITY_ARITHMETIC
  = TERM
  = TERM ("*" | "/") ARITHMETIC
*/
PRIORITY_ARITHMETIC.setPattern(
    lrec_sc(
        TERM,
        seq(alt_sc(tok(TokenKind.SymbolAsterisk), tok(TokenKind.SymbolForwardSlash)), ARITHMETIC),
        applyBinary,
    ),
);

/*
TERM
  = NUMBER
  = IDENTIFIERS
  = "-" ARITHMETIC
  = "(" TERM ")"
*/
TERM.setPattern(
    alt_sc(
        NUMBER,
        IDENTIFIERS,
        apply(seq(tok(TokenKind.SymbolMinus), ARITHMETIC), applyUnary),
        apply(seq(tok(TokenKind.SymbolOpenParen), TERM, tok(TokenKind.SymbolCloseParen)), applyParens),
    ),
);

/*
NUMBER
  = [0-9]+ ("." [0-9]+)?
*/
NUMBER.setPattern(apply(tok(TokenKind.LiteralNumber), applyNumber));

/*
IDENTIFIERS
  = "clicks"
  = "cpx"
*/
IDENTIFIERS.setPattern(
    apply(
        alt_sc(
            tok(TokenKind.IdentifierCost),
            tok(TokenKind.IdentifierClicks),
            tok(TokenKind.IdentifierInstalls),
            tok(TokenKind.IdentifierCPX),
        ),
        applyIdentifier,
    ),
);

/**
 * Transforms the token of a status into an AST.
 */
function applyStatus(token: Token<TokenKind>): AST {
    switch (token.kind) {
        case TokenKind.StatusActive:
            return { type: 'status', status: 'active' };
        case TokenKind.StatusPaused:
            return { type: 'status', status: 'paused' };
        case TokenKind.StatusSkip:
            return { type: 'status', status: 'skip' };
        default:
            throw new Error(`Unknown status: ${token.text}`);
    }
}

/**
 * Transforms the tokens of an if-then-else conditional into an AST.
 */
function applyConditional([_if, condition, _then, then, _else, else_]: [
    Token<TokenKind>,
    AST,
    Token<TokenKind>,
    AST,
    Token<TokenKind>,
    AST,
]): AST {
    return { type: 'conditional', condition, then, else: else_ };
}

/**
 * Transforms the tokens of a (and|or) combination into an AST.
 */
function applyCombination(left: AST, [operator, right]: [Token<TokenKind>, AST]): AST {
    switch (operator.kind) {
        case TokenKind.KeywordAnd:
            return { type: 'combination', operator: 'and', left, right };
        case TokenKind.KeywordOr:
            return { type: 'combination', operator: 'or', left, right };
        default:
            throw new Error(`Unknown combination operator: ${operator.text}`);
    }
}

function applyNot([operator, body]: [Token<TokenKind>, AST]): AST {
    switch (operator.kind) {
        case TokenKind.KeywordNot:
            return { type: 'not', body };
        default:
            throw new Error(`Unknown not operator: ${operator.text}`);
    }
}

/**
 * Transforms the tokens of a comparison into an AST.
 */
function applyComparison([left, operator, right]: [AST, Token<TokenKind>, AST]): AST {
    switch (operator.kind) {
        case TokenKind.SymbolLessThan:
            return { type: 'comparison', operator: '<', left, right };
        case TokenKind.SymbolGreaterThan:
            return { type: 'comparison', operator: '>', left, right };
        case TokenKind.SymbolLessThanEqual:
            return { type: 'comparison', operator: '<=', left, right };
        case TokenKind.SymbolGreaterThanEqual:
            return { type: 'comparison', operator: '>=', left, right };
        default:
            throw new Error(`Unknown comparison operator: ${operator.text}`);
    }
}

/**
 * Transforms the tokens of a unary operator into an AST.
 */
function applyUnary([operator, body]: [Token<TokenKind>, AST]): AST {
    switch (operator.kind) {
        case TokenKind.SymbolMinus:
            return { type: 'unary', operator: '-', body };
        default:
            throw new Error(`Unknown unary operator: ${operator.text}`);
    }
}

/**
 * Transforms the tokens of a binary operator into an AST.
 */
function applyBinary(left: AST, [operator, right]: [Token<TokenKind>, AST]): AST {
    switch (operator.kind) {
        case TokenKind.SymbolPlus:
            return { type: 'binary', operator: '+', left, right };
        case TokenKind.SymbolMinus:
            return { type: 'binary', operator: '-', left, right };
        case TokenKind.SymbolAsterisk:
            return { type: 'binary', operator: '*', left, right };
        case TokenKind.SymbolForwardSlash:
            return { type: 'binary', operator: '/', left, right };
        default:
            throw new Error(`Unknown binary operator: ${operator.text}`);
    }
}

/**
 * Transforms the tokens of a parenthesized expression into an AST.
 */
function applyParens([, body]: [Token<TokenKind>, AST, Token<TokenKind>]): AST {
    return { type: 'parens', body };
}

/**
 * Transforms the token of a number literal into an AST.
 */
function applyNumber(token: Token<TokenKind>): AST {
    return { type: 'number', value: Number(token.text) };
}

/**
 * Transforms the token of an identifier into an AST.
 */
function applyIdentifier(token: Token<TokenKind>): AST {
    switch (token.kind) {
        case TokenKind.IdentifierCost:
            return { type: 'identifier', name: 'cost' };
        case TokenKind.IdentifierClicks:
            return { type: 'identifier', name: 'clicks' };
        case TokenKind.IdentifierInstalls:
            return { type: 'identifier', name: 'installs' };
        case TokenKind.IdentifierCPX:
            return { type: 'identifier', name: 'cpx' };
        default:
            throw new Error(`Unknown identifier: ${token.text}`);
    }
}
